import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { useLayoutEffect, useRef, useState, type SyntheticEvent } from 'react';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import {
  assignIntranetImageFit,
  enlargeLabel,
} from '@/products/intranet/features/intranet/image-frame';

/** Full image, outside the 680 preview box. */
export function IntranetImageLightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  const title = alt.trim() || 'Bild';
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent className="w-fit max-w-[min(96vw,100%)] border-0 bg-transparent p-2 shadow-none sm:max-w-[min(96vw,100%)]">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">Vollständige Ansicht des Bildes.</DialogDescription>
        <img src={src} alt={alt} className="max-h-[90vh] w-auto max-w-[96vw] object-contain" />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reader size for a cover or inline image. Wide files stay 680 wide and scale in height.
 * Portrait files sit in a 680 square; the spare sides are a blurred copy of the same file.
 */
export function IntranetImageFrame({
  src,
  alt,
  expandable = false,
}: {
  src: string;
  alt: string;
  /** Article pages open the original. Previews stay at the preview size. */
  expandable?: boolean;
}) {
  const frameRef = useRef<HTMLSpanElement>(null);
  const photoRef = useRef<HTMLImageElement>(null);
  const [open, setOpen] = useState(false);

  function measure(image?: HTMLImageElement) {
    const frame = frameRef.current;
    const photo = image ?? photoRef.current;
    if (frame && photo) {
      assignIntranetImageFit(frame, photo);
    }
  }

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const photo = photoRef.current;
    if (frame) {
      delete frame.dataset.fit;
    }
    if (frame && photo) {
      assignIntranetImageFit(frame, photo);
    }
  }, [src]);

  const frame = (
    <span ref={frameRef} className="intranet-image-frame">
      <img src={src} alt="" aria-hidden className="intranet-image-blur" />
      <img
        ref={photoRef}
        src={src}
        alt={alt}
        className="intranet-image-photo h-auto w-full"
        onLoad={(event: SyntheticEvent<HTMLImageElement>) => measure(event.currentTarget)}
      />
    </span>
  );

  if (!expandable) {
    return frame;
  }

  return (
    <>
      <button
        type="button"
        className="intranet-image-open"
        aria-label={enlargeLabel(alt)}
        onClick={() => setOpen(true)}
      >
        {frame}
      </button>
      {open ? <IntranetImageLightbox src={src} alt={alt} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/** Editor surface uses the same preview frame. The stored markup stays a plain image. */
export function IntranetInlineImageView({ node }: NodeViewProps) {
  const src = typeof node.attrs.src === 'string' ? node.attrs.src : '';
  const alt = typeof node.attrs.alt === 'string' ? node.attrs.alt : '';
  if (!src) {
    return <NodeViewWrapper />;
  }
  return (
    <NodeViewWrapper className="my-2">
      <IntranetImageFrame src={src} alt={alt} />
    </NodeViewWrapper>
  );
}
